/* A destructive button that asks first, in place.
 *
 * The console does this inline in half a dozen places — "Remove" becomes
 * "Remove (keeps history)" and "Cancel" beside it — and it is the right pattern
 * for the same reason here: a modal is a thing to dismiss, and the sentence that
 * says what is about to happen ends up in it rather than on the button that does
 * it. This app has considerably more buttons that cannot be undone than the
 * console does, so the pattern is a component rather than eleven copies.
 *
 * The armed state is deliberately local and deliberately not shared: two armed
 * buttons on one page is fine, and the alternative — one "which button is armed"
 * value in a store — is how a confirmation ends up firing on the wrong row after
 * a list reorders.
 */
import * as Vue from 'vue';

export const ConfirmButton = {
  name: 'ConfirmButton',
  props: {
    // What the button says at rest.
    label: { type: String, required: true },
    // What it says once armed. Say what will happen, not "Are you sure" — the
    // second is a question the first has already answered.
    confirm: { type: String, required: true },
    size: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    // 'danger' is the default because that is what this exists for; 'quiet' is
    // for the reversible ones that still deserve a second press.
    kind: { type: String, default: 'danger' }
  },
  emits: ['confirmed'],
  setup: function (props, ctx) {
    var armed = Vue.ref(false);

    function arm() { armed.value = true; }
    function cancel() { armed.value = false; }
    function fire() {
      armed.value = false;
      ctx.emit('confirmed');
    }

    // A disabled button that is already armed would sit there armed with no way
    // to fire it, which reads as broken rather than as disabled.
    Vue.watch(function () { return props.disabled; }, function (off) {
      if (off) armed.value = false;
    });

    var restClass = Vue.computed(function () {
      return props.kind === 'danger' ? 'btn btn--danger-ghost' : 'btn btn--ghost';
    });

    var sizeClass = Vue.computed(function () {
      return props.size === 'sm' ? ' btn--sm' : '';
    });

    return {
      armed: armed, arm: arm, cancel: cancel, fire: fire,
      restClass: restClass, sizeClass: sizeClass
    };
  },
  template: [
    '<span class="confirm">',
    '  <button v-if="!armed" type="button" :class="restClass + sizeClass"',
    '    :disabled="disabled" @click="arm">{{ label }}</button>',
    '  <template v-else>',
    '    <button type="button" :class="\'btn btn--danger\' + sizeClass" @click="fire">',
    '      {{ confirm }}</button>',
    '    <button type="button" :class="\'btn btn--quiet\' + sizeClass" @click="cancel">',
    '      Cancel</button>',
    '  </template>',
    '</span>'
  ].join('\n')
};
